<template>

  <report-panel icon="$revenue" :title="$t('titles.revenue_breakdown')" :loading="loading">

    <v-row>
      <v-col class="text-end mb-5">
        <v-btn-toggle
          v-model="formState.mode"
          rounded="0"
          group
          divided
          variant="outlined"
          mandatory
          density="compact"
        >
          <v-btn v-for="modeOption in modeOptions" :value="modeOption.value">
            {{ modeOption.title }}
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <div ref="chartContainer">
      <bar
        :chart-options="chartOptions"
        :chart-data="chartData"
        :styles="chartStyles"
      />
    </div>

  </report-panel>

</template>

<script setup lang="ts">

import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import ReportPanel from './ReportPanel.vue'
import ChannelRegistry from '../../../../channels/ChannelRegistry'
import { Temporal } from '@js-temporal/polyfill'
import useFitChartToContainer from '../../../../utils/useFitChartToContainer'

// ChartJS
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, LineElement, LineController, PointElement, BarElement, CategoryScale, LinearScale } from 'chart.js'
import type { ChartData, ChartOptions, ChartDataset, TooltipItem } from 'chart.js'
import type { ReportingDateSummaries } from '../../../../services/api-clients/ReportingApi'
ChartJS.register(Title, Tooltip, Legend, LineElement, LineController, PointElement, BarElement, CategoryScale, LinearScale)

interface Props {
  loading: boolean
  dateSummaries: ReportingDateSummaries
  channels: string[]
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  channels: () => [],
})

const { n, t } = useI18n()

/**
 * Form
 */

interface FormState {
  mode: 'day' | 'month'
}

const formState = reactive<FormState>({
  mode: 'day',
})

/**
 * Chart
 */

const modeOptions = [
  {value: 'day', title: 'By Day'},
  {value: 'month', title: 'By Month'},
]

const chartOptions: ChartOptions<'bar'> = {
  plugins: {
    tooltip: {
      enabled: false,
      external: (context) => {

        // https://www.chartjs.org/docs/latest/configuration/tooltip.html#external-custom-tooltips
        // https://www.chartjs.org/docs/latest/samples/tooltip/html.html

        const { chart, tooltip: tooltipModel } = context
        const tooltip = getTooltipElement(chart)

        // Hide if no tooltip
        if (tooltipModel.opacity === 0) {
          tooltip.style.opacity = '0'
          return
        }

        /**
         * Build the content of the tooltip element
         */

        const dataPoint = tooltipModel.dataPoints[0]
        // @ts-ignore: Don't complain that DataSet has no "id" field
        const datasetId = dataPoint.dataset.id

        let formatter = null

        switch (datasetId) {
          case 'net_revenue':
          case 'commission':
            formatter = formatters.currency
            break
          case 'transactions':
            formatter = formatters.integer
            break
          default:
            console.warn(`unrecognized datasetId: "${datasetId}"`)
            return ''
        }

        const tooltipTitleElement = tooltip.querySelector('.tooltip-title')
        if (tooltipTitleElement) {
          (tooltipTitleElement as HTMLDivElement).innerText = `${context.tooltip.title[0]} – ${context.tooltip.dataPoints[0].dataset.label}`
        }

        const table = tooltip.querySelector('table')
        if (table) {
          const tableElements = buildTooltipElements(dataPoint, formatter)
          table.replaceChildren(...tableElements)
        }

        /**
         * Style and position the tooltip element
         */

        tooltip.style.opacity = '1'
        // tooltip.style.font = tooltipModel.options.bodyFont.string
        tooltip.style.padding = tooltipModel.options.padding + 'px ' + tooltipModel.options.padding + 'px'

        const { offsetHeight: tooltipHeight } = tooltip
        const { offsetLeft: canvasLeft, offsetTop: canvasTop, offsetHeight: canvasHeight } = chart.canvas

        // calculate the "natural" position of the tooltip
        let tooltipLeft = canvasLeft + tooltipModel.caretX
        let tooltipTop = canvasTop + tooltipModel.caretY

        // if the natural position causes the tooltip to flow off the canvas,
        // adjust
        const leftoverVerticalSpace = canvasHeight - tooltipTop - tooltipHeight
        if (leftoverVerticalSpace < 0) {
          tooltipTop += leftoverVerticalSpace
        }

        tooltip.style.left = tooltipLeft + 'px'
        tooltip.style.top = tooltipTop + 'px'

      },
    },
  },
  // aspectRatio: 16/9,
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      stacked: true,
    },
    y1: {
      stacked: true,
      ticks: {
        callback: (value, _index, _values) => {
          return n(value as number, {style: 'currency', currency: 'USD', maximumFractionDigits: 0})
        },
      },
      title: {
        display: true,
        text: t('titles.revenue'),
      },
    },
    y2: {
      axis: 'y',
      type: 'linear',
      position: 'right',
      title: {
        display: true,
        text: t('titles.number_of_transactions'),
      },
    },

  },
}

interface CollationGroup {
  label: string
  transactions: Collation
  net_revenue: Collation
  commission: Collation
}

interface Collation {
  total: number
  channelBreakdowns: Breakdown
  verticalBreakdowns: Breakdown
}

type Breakdown = {[key: string]: number}

const chartData = computed<ChartData<'bar', number[]>>(() => {

  // Build lists of all channels and verticals that are referenced anywhere
  // in the data being plotted. This allows us to populate a value of 0 into
  // the datasets in the event that a given channel/vertical is missing from a
  // particular date.

  const channelsToPlot = new Set<string>()
  const verticalsToPlot = new Set<string>()

  Object.values(props.dateSummaries).forEach((summary) => {
    
    const summaryChannels = new Set([
      ...Object.keys(summary.as_transaction_date.transactions.by_channel),
      ...Object.keys(summary.as_transaction_date.gross_revenue.by_channel),
      ...Object.keys(summary.as_transaction_date.net_revenue.by_channel),
    ])

    summaryChannels.forEach((channel) => {
      if (!props.channels.length || props.channels.includes(channel)) {
        channelsToPlot.add(channel)
      }
    })

    const summaryVerticals = new Set([
      ...Object.keys(summary.as_transaction_date.transactions.by_vertical),
      ...Object.keys(summary.as_transaction_date.gross_revenue.by_vertical),
      ...Object.keys(summary.as_transaction_date.net_revenue.by_vertical),
    ])

    summaryVerticals.forEach((vertical) => {
      verticalsToPlot.add(vertical)
    })

  })

  // Collate the data

  const collations: {[key: string]: CollationGroup} = {}

  Object.entries(props.dateSummaries).forEach(([dateStr, summary]) => {

    let key, label

    const date = Temporal.PlainDate.from(dateStr)

    switch (formState.mode) {
      case 'day':
        key = dateStr
        label = date.toLocaleString('en', {month: 'numeric', day: 'numeric'})
        break
      case 'month':
        key = dateStr.substring(0, 7)
        label = date.toLocaleString('en', {month: 'short', year: 'numeric'})
        break
    }

    if (!(key in collations)) {
      collations[key] = {
        label: label,
        transactions: {total: 0, channelBreakdowns: {}, verticalBreakdowns: {}},
        net_revenue: {total: 0, channelBreakdowns: {}, verticalBreakdowns: {}},
        commission: {total: 0, channelBreakdowns: {}, verticalBreakdowns: {}},
      }
    }

    const collation = collations[key]

    channelsToPlot.forEach((channel) => {

      // transactions
      const transactions = summary.as_transaction_date.transactions.by_channel[channel] ?? 0
      collation.transactions.total += transactions
      const transactionsSoFar = collation.transactions.channelBreakdowns[channel] ?? 0
      collation.transactions.channelBreakdowns[channel] = transactionsSoFar + transactions

      // net revenue
      const netRevenue = summary.as_transaction_date.net_revenue.by_channel[channel] ?? 0
      collation.net_revenue.total += netRevenue
      const netRevenueSoFar = collation.net_revenue.channelBreakdowns[channel] ?? 0
      collation.net_revenue.channelBreakdowns[channel] = netRevenueSoFar + netRevenue

      // commission
      const grossRevenue = summary.as_transaction_date.gross_revenue.by_channel[channel] ?? null
      const commission = grossRevenue !== null ? grossRevenue - netRevenue : 0
      collation.commission.total += commission
      const commissionSoFar = collation.commission.channelBreakdowns[channel] ?? 0
      collation.commission.channelBreakdowns[channel] = commissionSoFar + commission

    })

    verticalsToPlot.forEach((vertical) => {

      // transactions
      const transactions = summary.as_transaction_date.transactions.by_vertical[vertical] ?? 0
      const transactionsSoFar = collation.transactions.verticalBreakdowns[vertical] ?? 0
      collation.transactions.verticalBreakdowns[vertical] = transactionsSoFar + transactions

      // net revenue
      const netRevenue = summary.as_transaction_date.net_revenue.by_vertical[vertical] ?? 0
      const netRevenueSoFar = collation.net_revenue.verticalBreakdowns[vertical] ?? 0
      collation.net_revenue.verticalBreakdowns[vertical] = netRevenueSoFar + netRevenue

      // commission
      const grossRevenue = summary.as_transaction_date.gross_revenue.by_vertical[vertical] ?? null
      const commission = grossRevenue !== null ? grossRevenue - netRevenue : 0
      const commissionSoFar = collation.commission.verticalBreakdowns[vertical] ?? 0
      collation.commission.verticalBreakdowns[vertical] = commissionSoFar + commission

    })

  })

  // Build the datasets

  const transactionsDataset: ChartDataset<'line', number[]> = {
    // @ts-ignore: Don't complain that ChartDataset has no "id" field
    id: 'transactions',
    label: t('titles.transactions'),
    type: 'line',
    order: 1,
    backgroundColor: '#bc5090',
    borderColor: '#bc5090',
    borderWidth: 1,
    yAxisID: 'y2',
    data: Object.values(collations).map((collation) => collation.transactions.total),
    channelBreakdowns: Object.values(collations).map((collation) => collation.transactions.channelBreakdowns),
    verticalBreakdowns: Object.values(collations).map((collation) => collation.transactions.verticalBreakdowns),
  }

  const netRevenueDataset: ChartDataset<'bar', number[]> = {
    // @ts-ignore: Don't complain that ChartDataset has no "id" field
    id: 'net_revenue',
    label: t('titles.net_revenue'),
    type: 'bar',
    order: 2,
    backgroundColor: '#003f5c',
    data: Object.values(collations).map((collation) => collation.net_revenue.total),
    channelBreakdowns: Object.values(collations).map((collation) => collation.net_revenue.channelBreakdowns),
    verticalBreakdowns: Object.values(collations).map((collation) => collation.net_revenue.verticalBreakdowns),
  }

  const commissionDataset: ChartDataset<'bar', number[]> = {
    // @ts-ignore: Don't complain that ChartDataset has no "id" field
    id: 'commission',
    label: t('titles.commission'),
    type: 'bar',
    order: 2,
    backgroundColor: '#ffa600',
    data: Object.values(collations).map((collation) => collation.commission.total),
    channelBreakdowns: Object.values(collations).map((collation) => collation.commission.channelBreakdowns),
    verticalBreakdowns: Object.values(collations).map((collation) => collation.commission.verticalBreakdowns),
  }

  const chartData: ChartData<'bar', number[]> = {
    labels: Object.values(collations).map((collation) => collation.label),
    // @ts-ignore: Don't complain that we're adding ChartDataset<'line'> to ChartData<'bar'>
    datasets: [transactionsDataset, netRevenueDataset, commissionDataset],
  }

  return chartData

})

/**
 * Utility functions for custom tooltips
 */

function getTooltipElement(chart: ChartJS): HTMLDivElement {

  const canvasParent = chart.canvas.parentNode
  if (!canvasParent) {
    throw new Error('couldn\'t find canvasParent')
  }
  let tooltip = canvasParent ? canvasParent.querySelector('div') : null

  // Create element on first render
  if (!tooltip) {

    tooltip = document.createElement('div')
    tooltip.style.background = 'rgba(0, 0, 0, 0.7)'
    tooltip.style.borderRadius = '3px'
    tooltip.style.color = 'white'
    tooltip.style.opacity = '1'
    tooltip.style.pointerEvents = 'none'
    tooltip.style.position = 'absolute'
    tooltip.style.transform = 'translate(-50%, 0)'
    tooltip.style.transition = 'all .1s ease'

    const title = document.createElement('div')
    title.className = 'tooltip-title'
    tooltip.append(title)

    const table = document.createElement('table')
    table.className = 'tooltip-table'
    tooltip.append(table)

    canvasParent.appendChild(tooltip)

  }

  return tooltip

}

function buildTooltipElements(datapoint: TooltipItem<'bar'>, formatter: Formatter) {
  
  const elements = []

  // total

  const th = document.createElement('th')
  th.innerText = t('titles.total')

  const td = document.createElement('td')
  td.innerText = formatter(datapoint.raw as number)
  
  const tr = document.createElement('tr')
  tr.className = 'total'
  tr.append(th)
  tr.append(td)

  const tfoot = document.createElement('tfoot')
  tfoot.append(tr)

  elements.push(tfoot)

  // channel breakdown

  const channelTbody = document.createElement('tbody')
  // @ts-ignore: don't complain that Dataset doesn't have a channelBreakdowns field
  const channelBreakdown: Breakdown = datapoint.dataset.channelBreakdowns[datapoint.dataIndex]

  for (const [channelName, value] of Object.entries(channelBreakdown)) {
    
    const th = document.createElement('th')
    th.innerText = ChannelRegistry.find(channelName)?.titleShort ?? channelName // TODO: i18n

    const td = document.createElement('td')
    td.innerText = formatter(value)

    const tr = document.createElement('tr')
    tr.append(th)
    tr.append(td)

    channelTbody.append(tr)

  }

  elements.push(channelTbody)

  // vertical breakdown

  const verticalTbody = document.createElement('tbody')
  // @ts-ignore: don't complain that Dataset doesn't have a verticalBreakdowns field
  const verticalBreakdown: Breakdown = datapoint.dataset.verticalBreakdowns[datapoint.dataIndex]

  for (const [vertical, value] of Object.entries(verticalBreakdown)) {
    
    const th = document.createElement('th')
    th.innerText = t(`enums.verticals.${vertical}`)

    const td = document.createElement('td')
    td.innerText = formatter(value)

    const tr = document.createElement('tr')
    tr.append(th)
    tr.append(td)

    verticalTbody.append(tr)
    
  }

  elements.push(verticalTbody)

  return elements

}

type Formatter = (value: number) => string

const formatters: {[key: string]: Formatter} = {
  currency: (value) => n(value, 'currency'),
  integer: (value) => value.toString(),
}

/**
 * Adjust the chart size responsively
 * https://vue-chartjs.org/guide/#chart-with-dynamic-styles
 */

const container = ref(null) // template ref
const { chartStyles } = useFitChartToContainer(container)

</script>

<style lang="scss">

.tooltip-title {
  text-align: center;
  background-color: rgba(255, 255, 255, 0.25);
  padding: 5px;
  margin-bottom: 8px;
}

table.tooltip-table {

  border-collapse: collapse;
  width: 100%;

  th {
    text-align: left;
    padding: 4px 8px 4px 0;
  }

  td {
    text-align: right;
    padding: 4px 0;
  }

  tbody {

    tr:last-child {
      th, td {
        padding-bottom: 8px;
        border-bottom: 1px rgba(255, 255, 255, 0.25) solid;
      }
    }

    &:not(:first-of-type) {
      tr:first-child {
        th, td {
          padding-top: 8px;
        }
      }
    }

  }

  tfoot {
    th, td {
      padding-top: 8px;
    }
  }

}

</style>
